import React, { useEffect, useRef } from 'react'

import styles from './ListeningIndicator.module.scss'

interface ListeningIndicatorProps {
  audioStream?: MediaStream | null
}

const ListeningIndicator: React.FC<ListeningIndicatorProps> = ({
  audioStream,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const colorRef = useRef<string | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const animationFrameRef = useRef<number | null>(null)

  const drawWaveform = () => {
    if (!canvasRef.current || !analyserRef.current) return

    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const analyser = analyserRef.current
    const bufferLength = analyser.frequencyBinCount
    const dataArray = new Uint8Array(bufferLength)

    analyser.getByteFrequencyData(dataArray)

    ctx.clearRect(0, 0, canvas.width, canvas.height)
    if (!colorRef.current) {
      const color = getComputedStyle(canvas)
        .getPropertyValue('--waveform-color')
        .trim()
      colorRef.current = color
    }
    ctx.fillStyle = colorRef.current
    ctx.lineWidth = 1

    const barSpacing = 1
    const barCount = 7

    for (let i = 0; i < barCount; i++) {
      // Logarithmic scale for better frequency distribution
      const minFreq = 2 // Skip very low frequencies
      const maxFreq = bufferLength
      const logIndex = minFreq * (maxFreq / minFreq)**(i / (barCount - 1))
      const dataIndex = Math.min(Math.floor(logIndex), bufferLength - 1)

      const value = dataArray[dataIndex]

      // Apply threshold to filter out background noise
      const threshold = 8
      // eslint-disable-next-line no-continue
      if (value < threshold) continue

      // Calculate bar height with scaling that boosts higher frequencies
      const normalizedValue = (value - threshold) / (255 - threshold)
      const frequencyBoost = 1 + i * 0.15
      const barHeight =
        Math.sqrt(normalizedValue) * canvas.height * 0.8 * frequencyBoost

      // eslint-disable-next-line no-continue
      if (barHeight < 1) continue

      const x = (canvas.width / barCount) * i + barSpacing
      const barW = canvas.width / barCount - barSpacing * 2

      const centerY = canvas.height / 2
      const barY = centerY - barHeight / 2

      ctx.fillRect(x, barY, barW, barHeight)
    }

    animationFrameRef.current = requestAnimationFrame(drawWaveform)
  }

  useEffect(() => {
    if (!audioStream) return undefined

    const startAudioVisualization = async () => {
      try {
        const stream = audioStream

        const AudioContext =
          window.AudioContext || (window as any).webkitAudioContext
        const audioContext = new AudioContext()
        audioContextRef.current = audioContext

        const analyser = audioContext.createAnalyser()
        analyser.fftSize = 256
        analyser.smoothingTimeConstant = 0.8
        analyserRef.current = analyser

        const source = audioContext.createMediaStreamSource(stream)
        source.connect(analyser)

        drawWaveform()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to start audio visualization:', error)
      }
    }

    startAudioVisualization()

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }

      if (audioContextRef.current) {
        audioContextRef.current.close()
      }
    }
  }, [audioStream])

  return (
    <div className={styles.container}>
      <canvas
        className={styles.canvas}
        height={28}
        ref={canvasRef}
        width={28}
      />
    </div>
  )
}

export default ListeningIndicator
